import { useEffect, useMemo, useState } from 'react';
import { Save, Video } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import { Label } from '@/components/admin/ui/label';
import { Badge } from '@/components/admin/ui/badge';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  VIDEO_ADDON_DB_CODE,
  VIDEO_ADDON_MIN_ADVANCE_DAYS,
  VIDEO_ADDON_PRICING,
  getVideoAddOnDeliverables,
  normalizeVideoAddOnPricing
} from '@/shared/videoAddOnPricing';

type PriceMap = Record<string, number>;

type AddonRow = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price_cents: number;
  metadata: Record<string, any> | null;
};

const AddOnPricing = () => {
  const [addon, setAddon] = useState<AddonRow | null>(null);
  const [prices, setPrices] = useState<PriceMap>(VIDEO_ADDON_PRICING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const hasInvalidPrice = useMemo(
    () => Object.values(prices).some(value => !Number.isFinite(Number(value)) || Number(value) <= 0),
    [prices]
  );

  useEffect(() => {
    loadVideoAddOn();
  }, []);

  const loadVideoAddOn = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseAdmin
        .from('addons')
        .select('id, code, name, description, price_cents, metadata')
        .eq('code', VIDEO_ADDON_DB_CODE)
        .maybeSingle();

      if (error) throw error;

      setAddon(data as AddonRow | null);
      setPrices(normalizeVideoAddOnPricing(data?.metadata?.priceByHours));
    } catch (error) {
      console.error('Error loading video add-on pricing:', error);
      toast({
        title: 'Error',
        description: 'Failed to load video add-on pricing.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePrice = (hour: string, value: string) => {
    const numericValue = Number(value);
    setPrices(prev => ({
      ...prev,
      [hour]: Number.isFinite(numericValue) ? numericValue : 0
    }));
  };

  const saveVideoAddOn = async () => {
    if (hasInvalidPrice) {
      toast({
        title: 'Invalid pricing',
        description: 'Every hour must have a price greater than $0.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      const normalizedPrices = normalizeVideoAddOnPricing(prices);
      const metadata = {
        ...(addon?.metadata || {}),
        pricingModel: 'hour_selector',
        requiresPhotoOnlyBooking: true,
        requires_advance_notice_days: VIDEO_ADDON_MIN_ADVANCE_DAYS,
        minHours: 1,
        maxHours: 8,
        priceByHours: normalizedPrices,
        deliverables: {
          '1': getVideoAddOnDeliverables(1),
          '2_plus': getVideoAddOnDeliverables(2)
        }
      };

      const payload = {
        code: VIDEO_ADDON_DB_CODE,
        name: addon?.name || 'Video Add-On',
        description: addon?.description || 'Optional video coverage upgrade for photo-only bookings. Couples choose 1 to 8 hours of coverage.',
        price_cents: Math.round(normalizedPrices[1] * 100),
        kind: 'addon',
        display_order: 12,
        icon_name: 'Video',
        metadata,
        active: true,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabaseAdmin
        .from('addons')
        .upsert(payload, { onConflict: 'code' })
        .select('id, code, name, description, price_cents, metadata')
        .single();

      if (error) throw error;

      setAddon(data as AddonRow);
      setPrices(normalizedPrices);
      toast({
        title: 'Saved',
        description: 'Video add-on pricing has been updated.',
      });
    } catch (error) {
      console.error('Error saving video add-on pricing:', error);
      toast({
        title: 'Error',
        description: 'Failed to save video add-on pricing.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Add-On Pricing</h1>
        <p className="text-muted-foreground">
          Manage dynamic add-on pricing used by the client portal and payment API.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Video className="h-5 w-5" />
                Video Add-On
              </CardTitle>
              <CardDescription>
                Photo-only booking upgrade. Within 15 days of the event a $450 late booking fee applies per added team member.
              </CardDescription>
            </div>
            <Badge variant="outline">1-8 hours</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading pricing...</div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {Object.keys(VIDEO_ADDON_PRICING).map(hour => {
                  const hourCount = Number(hour);
                  return (
                    <div key={hour} className="space-y-2 rounded-md border p-3">
                      <Label htmlFor={`video-addon-${hour}`}>
                        {hourCount} hour{hourCount !== 1 ? 's' : ''}
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                        <Input
                          id={`video-addon-${hour}`}
                          type="number"
                          min="1"
                          step="1"
                          value={prices[hour] ?? ''}
                          onChange={event => updatePrice(hour, event.target.value)}
                          className="pl-7"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {hourCount === 1 ? 'Unedited ceremony footage' : 'Includes highlight film'}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground">
                The 1-hour price also updates the catalog base price. Checkout still charges from the full hour table.
              </div>

              <Button onClick={saveVideoAddOn} disabled={saving || hasInvalidPrice}>
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Pricing'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AddOnPricing;
